import type { VoidComponent } from "solid-js";
import { createMemo } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { isFinite, isNil } from "lodash-es";

type NumberProps = {
  id: string;
  ariaLabel?: string;
  value: number | null;
  allowDecimal?: boolean;
  errorMessage?: string;
  onBlur: (value: number | null) => void;
  onInput?: (value: number | null) => void;
  minValue: number;
  maxValue: number;
  disabled?: boolean;
};
type NumberInputProps = NumberProps & ExtendableInputProps;

const BaseNumberInput: VoidComponent<NumberProps> = (props) => {
  const hasError = () => props.errorMessage !== undefined;
  const isDisabled = createMemo(() => props.disabled === true);
  const classes = createMemo(() => {
    if (hasError()) {
      return "bg-surface-destructive border-error";
    } else if (isDisabled()) {
      return "bg-surface-disabled border-disabled";
    } else {
      return "bg-surface-default border-default";
    }
  });

  return (
    <input
      id={props.id}
      aria-label={props.ariaLabel}
      min={props.minValue}
      max={props.maxValue}
      type="number"
      class="no-arrows w-full truncate rounded-sm border p-2 text-base-regular text-primary focus-visible:outline-default"
      classList={{ [classes()]: true }}
      disabled={isDisabled()}
      value={props.value ?? ""}
      onKeyDown={(e) => {
        const target = e.target as HTMLInputElement;
        if (
          e.key.length === 1 &&
          isNaN(Number(e.key)) &&
          !["Backspace", "Delete", "Tab", "Escape", "Enter", ".", ",", "-"].includes(e.key) &&
          !(e.key === "a" && (e.metaKey || e.ctrlKey)) &&
          !(e.key === "c" && (e.metaKey || e.ctrlKey)) &&
          !(e.key === "v" && (e.metaKey || e.ctrlKey)) &&
          !(e.key === "x" && (e.metaKey || e.ctrlKey))
        ) {
          e.preventDefault();
          return;
        }

        // Cut off input when exceeding MAX_SAFE_INTEGER
        const currentValue = target.value;
        if (e.key.length === 1 && !isNaN(Number(e.key))) {
          const resultingValue = currentValue + e.key;
          if (Math.abs(Number(resultingValue)) > Number.MAX_SAFE_INTEGER) {
            e.preventDefault();
            return;
          }
        }

        if (props.allowDecimal === true) {
          return;
        }
        if (e.key === "." || e.key === ",") {
          e.preventDefault();
        }
      }}
      onPaste={(e) => {
        const target = e.target as HTMLInputElement;
        const pastedText = e.clipboardData?.getData("text");
        if (isNil(pastedText)) {
          return;
        }

        // Ignore paste when exceeding MAX_SAFE_INTEGER
        const resultingValue = (target.value + pastedText).replace(",", ".");
        if (isNaN(Number(resultingValue)) || Math.abs(Number(resultingValue)) > Number.MAX_SAFE_INTEGER) {
          e.preventDefault(); // Prevent the paste
          return;
        }
      }}
      onInput={(e) => {
        const value = Number.parseFloat(e.target.value);
        props.onInput?.(isFinite(value) ? value : null);
      }}
      onBlur={(e) => {
        const value = Number.parseFloat(e.target.value);
        props.onBlur(isFinite(value) ? value : null);
      }}
    />
  );
};

export const NumberInput: VoidComponent<Omit<NumberInputProps, "id">> = (props) => (
  <Input {...props}>{(id) => <BaseNumberInput {...props} id={id} />}</Input>
);

export const CompactNumberInput: VoidComponent<NumberProps> = (props) => <BaseNumberInput {...props} />;
